/**
 * Theme bridge for the schedule pattern editor dialog. Every design value the LESS reads is declared
 * here, mapped from DesignSystem tokens onto `--local-schedule-editor-*` properties scoped to the
 * dialog element.
 *
 * FOUR STEPS, and nothing else. Every piece of text in the dialog is exactly one of:
 *
 *   title    16 / 600           `textLg`      — the dialog heading
 *   label    12 / 600 muted     `textSm`      — section labels
 *   control  13 / 500           `textMdBase`  — values, chips, buttons, body copy
 *   caption  11 / 400 muted     literal 11px  — hints, eyebrow, notes
 *
 * Day and month segments are the one deliberate exception at 11 / 600: seven of them share a row, so
 * they need the smaller face to fit without truncating.
 *
 * Two things here are literals rather than tokens, on purpose. `textXs` is 10px, which is a step below
 * the 11px the boards specify for captions, so captions carry an explicit 11px. And the host sets an
 * explicit base `font-size` — without it, any text whose own rule is missing inherits the host page's
 * 16px default, which is precisely the "fonts look weird / one size too large" symptom this fixes.
 */

import { DesignSystem } from '@workbuddy/ui/v2/design-system';

const tokens = DesignSystem.tokens;

export const scheduleEditorStyleId = 'wb-v2-schedule-editor-theme';

export const scheduleEditorBridgeStyles = {
  'wb-schedule-editor-dialog': {
    '--local-schedule-editor-bg': tokens.color.background.primary,
    '--local-schedule-editor-font': tokens.font.family.body,
    '--local-schedule-editor-title': tokens.color.text.primary,
    '--local-schedule-editor-label': tokens.color.text.secondary,
    '--local-schedule-editor-muted': tokens.color.text.tertiary,
    // text.quaternary (~gray-400) is ~2.4:1 on white — fails WCAG AA for 11px caps. The task name must
    // be readable, not decorative: secondary text keeps the muted register at a compliant contrast.
    '--local-schedule-editor-eyebrow': tokens.color.text.secondary,
    '--local-schedule-editor-link': tokens.color.text.brandSecondary,
    '--local-schedule-editor-border': tokens.color.border.secondary,
    '--local-schedule-editor-card-border': tokens.color.border.primary,
    '--local-schedule-editor-card-border-active': tokens.color.border.brand,
    '--local-schedule-editor-card-bg': tokens.color.background.primary,
    '--local-schedule-editor-card-bg-active': tokens.color.background.brandPrimary,

    '--local-schedule-editor-pad': tokens.spacing.xl,
    '--local-schedule-editor-block-gap': tokens.spacing.xl,
    '--local-schedule-editor-gap': tokens.spacing.lg,
    '--local-schedule-editor-gap-md': tokens.spacing.xl, // 16px — air around the mode switch

    // NOTE: repointing `--local-toggle-*` here does NOT work, and the properties are deliberately not
    // declared. Both bridges emit plain element-type rules into <head>, and the component's own bridge
    // sets `--local-toggle-*` ON `wb-button-toggle` itself. A value declared on an ancestor is only
    // INHERITED, and inheritance always loses to a declaration on the element — so the component's
    // brandSolid keeps winning inside the dialog no matter what the dialog root says. The hover states
    // are therefore fixed in the LESS, by out-specifying the component's rules (see the hover block
    // beside `.selection-active()`), not by trying to re-point its custom properties from up here.

    // wb-button-radio defaults its labels to 12px secondary text — too quiet for the dialog's primary
    // options ("Every visit").
    //
    // These two are subject to the same inheritance trap described above: `button.radio.theme.ts`
    // declares `--local-radio-label-*` on `wb-button-radio` itself, so these root values are only
    // inherited and never reach the component. They are left in place because they are inert rather
    // than wrong, and changing the radio labels is not part of this fix — but do NOT copy this shape
    // as a working example. Anything that must actually beat a v2 component belongs in the LESS,
    // out-specifying the component's own rule.
    '--local-radio-label-font-size': tokens.font.size.textMdBase,
    '--local-radio-label-color': tokens.color.text.primary,
    '--local-schedule-editor-gap-sm': tokens.spacing.md,
    '--local-schedule-editor-gap-xs': tokens.spacing.sm,
    '--local-schedule-editor-gap-xxs': tokens.spacing.xxs,
    '--local-schedule-editor-radius': tokens.radius.md,

    // Base size for the subtree. Anything without its own rule lands here rather than on the host
    // page's 16px default.
    '--local-schedule-editor-base-size': tokens.font.size.textMdBase,
    '--local-schedule-editor-base-line': tokens.lineHeight.textMd,

    // Step 1 — title.
    '--local-schedule-editor-title-size': tokens.font.size.textLg,
    '--local-schedule-editor-title-weight': tokens.font.weight.semibold,
    '--local-schedule-editor-title-line': tokens.lineHeight.textLg,

    // Step 2 — section label.
    '--local-schedule-editor-label-size': tokens.font.size.textSm,
    '--local-schedule-editor-label-weight': tokens.font.weight.semibold,
    '--local-schedule-editor-label-line': tokens.lineHeight.textSm,

    // Step 3 — control text and body copy.
    '--local-schedule-editor-body-size': tokens.font.size.textMdBase,
    '--local-schedule-editor-body-weight': tokens.font.weight.medium,
    '--local-schedule-editor-body-line': tokens.lineHeight.textMd,

    // Step 4 — caption. 11px literal: `textXs` is 10px, a step below what the boards call for.
    '--local-schedule-editor-caption-size': '11px',
    '--local-schedule-editor-caption-weight': tokens.font.weight.regular,
    '--local-schedule-editor-caption-line': tokens.lineHeight.textXs,

    // The eyebrow is a caption that happens to be small-capped.
    '--local-schedule-editor-eyebrow-size': '11px',
    '--local-schedule-editor-eyebrow-weight': tokens.font.weight.medium,
    '--local-schedule-editor-card-title-weight': tokens.font.weight.medium,

    // Repeat chips: 28px tall with 5px/10px padding, which keeps ~8px between the text and the border
    // at 12px type. Literals rather than spacing tokens — spacing.xs/md are 4px/8px and land the text
    // closer to the edge than the boards allow.
    '--local-schedule-editor-chip-height': '32px', // one strip height with kinds/ends/unit — mixed 28/32 read as uneven buttons
    '--local-schedule-editor-chip-pad-y': '5px',
    '--local-schedule-editor-chip-pad-x': '10px',
    '--local-schedule-editor-chip-size': tokens.font.size.textSm,
    '--local-schedule-editor-chip-weight': tokens.font.weight.medium,
    '--local-schedule-editor-chip-radius': tokens.radius.md,
    // The repeat row's wrap point. Seven chips at a 96px basis and the 6px row gap fit four per row in
    // the dialog's 448px content box (4 × 96 + 3 × 6 = 402; a fifth needs 504), so the row lands a
    // balanced 4 + 3 rather than stranding 'Specific dates' alone on a second line. `flex-grow` then
    // stretches both rows to full width, so the short second row does not read as an orphan.
    '--local-schedule-editor-repeat-min-width': tokens.spacing.nineXl,

    // Selection state for every bordered chip and segment in the dialog. One language: a tinted fill,
    // a tinted border and brand text — never a ring, which is reserved for keyboard focus.
    '--local-schedule-editor-select-idle-bg': tokens.color.background.secondary,
    '--local-schedule-editor-select-idle-border': tokens.color.border.secondary,
    '--local-schedule-editor-select-idle-text': tokens.color.text.secondary,
    '--local-schedule-editor-select-active-bg': tokens.componentColors.utility.brand50,
    '--local-schedule-editor-select-active-border': tokens.componentColors.utility.brand200,
    '--local-schedule-editor-select-active-text': tokens.componentColors.utility.brand700,
    '--local-schedule-editor-select-active-weight': tokens.font.weight.semibold,

    // Hover is a nudge, never a state change. An idle segment warms one step off its own idle fill,
    // and a selected segment warms one step off brand-50 — it must NOT jump to a solid brand fill,
    // which is the defect this pair exists to close: the component's checked hover is brandSolidHover,
    // and with the dialog's brand-700 label still winning the colour, the label vanished into the fill.
    '--local-schedule-editor-select-idle-hover-bg': tokens.color.background.secondaryHover,
    '--local-schedule-editor-select-active-hover-bg': tokens.componentColors.utility.brand100,

    // Day / month / ordinal segments share one height so the rows stack evenly. Seven share a row, so
    // they take the one deliberate exception to the four-step scale: 11/600.
    '--local-schedule-editor-segment-height': '36px',
    '--local-schedule-editor-segment-size': '11px',
    '--local-schedule-editor-segment-weight': tokens.font.weight.semibold,

    // Two text lines plus padding — the floor that keeps all four fortnight cards the same size.
    '--local-schedule-editor-card-height': '56px',

    // Kind tabs are the dialog's primary navigation, so the active one reads as a solid brand button
    // rather than the tab component's default outline. Scoped to this strip only — the global tab
    // component keeps its own default look everywhere else.
    // Header close, matching the skip/extra popovers' cross so the two dismissals look alike.
    '--local-schedule-editor-close-color': tokens.color.foreground.quaternary,
    '--local-schedule-editor-close-hover-color': tokens.color.foreground.secondary,
    '--local-schedule-editor-close-size': '24px',

    '--local-schedule-editor-tab-active-bg': tokens.color.background.brandSolid,
    '--local-schedule-editor-tab-active-hover-bg': tokens.color.background.brandSolidHover,
    '--local-schedule-editor-tab-active-border': tokens.color.border.brand,
    '--local-schedule-editor-tab-active-text': tokens.color.text.primaryOnBrand,
    '--local-schedule-editor-tab-height': '32px',
    '--local-schedule-editor-tab-size': tokens.font.size.textSm,
    '--local-schedule-editor-tab-weight': tokens.font.weight.semibold,

    // The after-service row is one assembly: stepper, unit picker and caption share a height and a
    // border family so the eye reads a single control, not three stacked ones.
    '--local-schedule-editor-control-height': '32px', // stepper, unit segments and date field sit level with the strips
    '--local-schedule-editor-control-size': tokens.font.size.textSm,
    '--local-schedule-editor-control-weight': tokens.font.weight.medium,
    '--local-schedule-editor-control-radius': tokens.radius.sm,
    '--local-schedule-editor-control-border': tokens.color.border.primary,
    '--local-schedule-editor-control-bg': tokens.color.background.primary,
    '--local-schedule-editor-control-text': tokens.color.text.primary,

    // Date field: a bordered field wide enough for the widest label it carries — the First due
    // default, 'With the Service Line · Tue 1 Sep' — without reflowing onto a second line.
    '--local-schedule-editor-datefield-width': '260px',
    '--local-schedule-editor-datefield-placeholder': tokens.color.text.placeholder,
    '--local-schedule-editor-datefield-hover-bg': tokens.color.background.secondary,
    '--local-schedule-editor-datefield-icon-size': tokens.font.size.textSm,

    // The explicit-date list. Rows are quiet — the dates are the content, and a boxed row per date
    // would turn a two-date list into a wall.
    '--local-schedule-editor-date-row-gap': tokens.spacing.md,
    '--local-schedule-editor-date-row-pad-y': tokens.spacing.xs,
    '--local-schedule-editor-date-icon': tokens.color.foreground.quaternary,
    '--local-schedule-editor-date-icon-size': tokens.font.size.textSm,
    '--local-schedule-editor-date-label': tokens.color.text.primary,
    '--local-schedule-editor-date-label-size': tokens.font.size.textMdBase,
    '--local-schedule-editor-date-label-line': tokens.lineHeight.textMd,
    '--local-schedule-editor-date-remove': tokens.color.foreground.quaternary,
    '--local-schedule-editor-date-remove-hover': tokens.color.foreground.errorPrimary,
    '--local-schedule-editor-date-divider': tokens.color.border.secondary,

    // The blocking-issue line in the footer, in place of the effective-from note.
    '--local-schedule-editor-issue': tokens.color.text.errorPrimary,

    '--local-schedule-editor-focus': tokens.focusRing.default,
  },

  // The first-due calendar renders in the CDK overlay container, a sibling of the dialog rather than a
  // descendant, so the dialog-scoped properties above never reach it. It carries its own bridge.
  '.editor-datepopover': {
    '--local-schedule-editor-popover-bg': tokens.color.background.primary,
    '--local-schedule-editor-popover-border': tokens.color.border.secondary,
    '--local-schedule-editor-popover-radius': tokens.radius.lg,
    '--local-schedule-editor-popover-shadow': tokens.shadow.lg,
    '--local-schedule-editor-popover-pad': tokens.spacing.md,
  },
};
